import { DisplayProcessingLogics, PoolDeviceCriticalDesiredActual } from "../Types";
import { IOnoffable, PoolDeviceInterface } from "../Interfaces";
import Locale from "../../utils/Locale";
import { DeviceTypes, DeviceConfig } from "../../config/documents/HardwareConfig";
import PoolDeviceOnOffable from "./base-onoffable";
import { ValueAndUnit } from "../../types";
import { convertConfigHvac } from "../common-hvac";
import { separator } from "../../types";


export enum criticalFieldIdentifiers {
    OnOff = "OnOff",
    HVACMode = "HVACMode",   
}

export default class PoolDeviceHeatPump extends PoolDeviceOnOffable implements PoolDeviceInterface, IOnoffable {
    static CanBeAddedToflow = true;
    static DeviceType = DeviceTypes.HEATPUMP;
    static Icon = "fan";
    static PickerTitle = Locale.gettext("Heat Pump");
    OnOffIdentifier = criticalFieldIdentifiers.OnOff;

    getDisplayNameTranslated(displayName:string) {
        switch (displayName) {
        case "Status": return Locale.gettext("Status");
        case "Mode": return Locale.gettext("Mode");
        case "Inlet Water": return Locale.gettext("Inlet Water");
        case "Inlet": return Locale.gettext("Inlet");
        case "Outlet Water": return Locale.gettext("Outlet Water");
        case "Outlet": return Locale.gettext("Outlet");
        case "Ambient Air": return Locale.gettext("Ambient Air");
        case "Ambient": return Locale.gettext("Ambient");
        case "Cool Setpoint": return Locale.gettext("Cool Setpoint");
        case "Heat Setpoint": return Locale.gettext("Heat Setpoint");
        case "Coil Temperature": return Locale.gettext("Coil Temperature");
        case "Coil Temp": return Locale.gettext("Coil Temp");
        case "Defrosting": return Locale.gettext("Defrosting");
        case "Transformer Output": return Locale.gettext("Transformer Output");
        case "Xfrmr": return Locale.gettext("Xfrmr");
        case "Heat Cycles": return Locale.gettext("Heat Cycles");
        case "Heat Hours": return Locale.gettext("Heat Hours");
        case "Cool Cycles": return Locale.gettext("Cool Cycles");
        case "Cool Hours": return Locale.gettext("Cool Hours");
        case "Power Cycles": return Locale.gettext("Power Cycles");
        case "Powerfails": return Locale.gettext("Powerfails");
        case "Capability": return Locale.gettext("Capability");
        case "BTU Output": return Locale.gettext("BTU Output");
        case "BTU": return Locale.gettext("BTU");
        case "Serial Number": return Locale.gettext("Serial Number");
        case "Serial": return Locale.gettext("Serial");
        case "Model Number": return Locale.gettext("Model Number");
        case "Model": return Locale.gettext("Model");
        case "Firmware": return Locale.gettext("Firmware");
        case "FW": return Locale.gettext("FW");       
        default: return super.getDisplayNameTranslated(displayName);
        }
    }

    formatDeviceStatusCustom(DisplayProcessingLogic: DisplayProcessingLogics, value: any): ValueAndUnit {
        switch(DisplayProcessingLogic) {
        case DisplayProcessingLogics.HVAC_MODE: return { value: getHavcModeLocalised(value) };
        case DisplayProcessingLogics.HEATPUMP_FRIENDLY_STATE: return { value: this.getFriendlyStateLocalised(value) };
        case DisplayProcessingLogics.HEATPUMP_OPERATING_MODE: return { value: this.getOperatingModeLocalised(value) };
        case DisplayProcessingLogics.HEATPUMP_CAPABILITY: return { value: this.getCapabilityLocalised(value) };
        default: return super.formatDeviceStatusCustom(DisplayProcessingLogic, value);
        }
    }

    getFriendlyStateLocalised(value: string): string {
        switch (value) {
        case 'INITIALIZATION': return Locale.gettext('Initializing');
        case 'NO_DEMAND': return Locale.gettext('No demand');
        case 'COMPRESSOR_START_DELAY': return Locale.gettext('Compressor Start Delay');
        case 'VALVE_CHANGE_DELAY': return Locale.gettext('Valve Change Delay');
        case 'VERIFYING_WATER': return Locale.gettext('Verifying Water Flow');
        case 'DEFROST': return Locale.gettext('Defrosting');
        case 'HEATING': return Locale.gettext('Heating');
        case 'COOLING': return Locale.gettext('Cooling');
        case 'SOLAR_HEATER': return Locale.gettext('Solar Heating');
        case 'FAULT': return Locale.gettext('Fault');
        case 'OUTSIDE_TEMP_LOCKOUT': return Locale.gettext('Outside Temperature Lockout');
        case 'UNKNOWN': return Locale.gettext('Unknown');
        default: return value;
        }
    }
    
    getOperatingModeLocalised(value: string): string {
        switch (value) {
        case 'OFF': return Locale.gettext('Off');
        case 'POOL_HEAT': return Locale.gettext('Pool Heating');
        case 'SPA_HEAT_COOL': return Locale.gettext('Spa Heat/Cool');
        case 'POOL_AUTO': return Locale.gettext('Pool Auto');
        case 'POOL_COOL': return Locale.gettext('Pool Cooling');
        case 'AUTO': return Locale.gettext('Auto');
        case 'HEAT': return Locale.gettext('Heating');
        case 'COOL': return Locale.gettext('Cooling');
        case 'UNKNOWN': return Locale.gettext('Unknown');
        default: return value;
        }
    }

    getCapabilityLocalised(value: string): string {
        switch (value) {
        case 'UNKNOWN': return Locale.gettext('Unknown');
        case 'HEAT': return Locale.gettext('Heating');
        case 'HEAT_COOL': return Locale.gettext('Heat and Cool');
        default: return value;
        }
    }

    getCriticalFieldSummaryCustom(): string {
        const criticalFieldsPropertyAndValue = this.getCriticalFieldsPropertyAndValue() || [];
        return criticalFieldsPropertyAndValue.reduce((acc, curr) => (
            // Don't show HVAC Mode in case it is "Off"
            (
                curr.Identifier === criticalFieldIdentifiers.HVACMode &&
                curr.ActualPropertyAndValue.Value === PoolsideDaemonHvacModes.OFF
            ) ?
                acc :
                (acc ? (acc + " " + separator + " ") : "") +
                // curr.ActualPropertyAndValue.Property.DisplayNameShort + ': ' +
                curr.ActualPropertyAndValue.ValueFormatted
        ), "");
    }

    getConfig(AutoFilterVersion?: number): DeviceConfig {
        return convertConfigHvac(this.deviceConfig, AutoFilterVersion);
    }

    setCriticalFieldOnOff(isOn: boolean) {
        const criticalFieldHvac = this.getCriticalFieldPropertyAndValue(criticalFieldIdentifiers.HVACMode);
        // Set HVAC mode if not set or set to OFF or UNKNOWN
        // Controller won't turn it on otherwise
        if (
            !criticalFieldHvac.LocalDesiredPropertyAndValue.Value ||
            criticalFieldHvac.LocalDesiredPropertyAndValue.Value === PoolsideDaemonHvacModes.OFF ||
            criticalFieldHvac.LocalDesiredPropertyAndValue.Value === PoolsideDaemonHvacModes.UNKNOWN
        ) {
            const supportedModes = JSON.parse(this.getDeviceState(PoolsideDaemonDeviceStateNames.SupportedModes)?.value || "[]") || [];
            const defaultMode = PoolsideDaemonHvacModesOrder.find(item => supportedModes.includes(item));
            this.setCriticalField(criticalFieldIdentifiers.HVACMode, defaultMode);
        }
        super.setCriticalFieldOnOff(isOn);
    }

    getCriticalFields():PoolDeviceCriticalDesiredActual[] {
        let order = 0;
        const fields = [];
    
        {
            const DisplayName = Locale.gettext("Power");
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.ONOFF,
                DisplayProcessingLogicUnitAsDisplayName: false,
                DisplayDiffTimeout: 300,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.OnOff,
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.ActualPowerState }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.PowerState }, common)
            });
        }
        {
            const DisplayName = Locale.gettext("HVAC Mode");
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.HVAC_MODE,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.HVACMode,
                // Controller does not provide actual state
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.DesiredHVACMode }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.DesiredHVACMode }, common)
            });
        }
        
        return fields;
    }
}

export enum PoolsideDaemonDeviceStateNames { 
    ActualPowerState = "ActualPowerState",
    DesiredHVACMode = "DesiredHVACMode",
    PowerState = "PowerState",
    SupportedModes = "SupportedModes",
}

export enum PoolsideDaemonHvacModes {
    ELECTRIC_HEAT = "ELECTRIC_HEAT",
    ELECTRIC_COOL = "ELECTRIC_COOL",
    GAS_HEAT = "GAS_HEAT",
    ELECTRIC_HEAT_AND_GAS_HEAT = "ELECTRIC_HEAT_AND_GAS_HEAT",
    SOLAR = "SOLAR",
    /** @deprecated Removed in AutoFilterVersion v8. */
    COOL = "COOL",
    /** @deprecated Removed in AutoFilterVersion v8. */
    HEAT = "HEAT",
    OFF = "OFF",
    UNKNOWN = "UNKNOWN",
}

export function getHavcModeLocalised(value: string): string {
    switch (value) {
    case PoolsideDaemonHvacModes.ELECTRIC_HEAT: return Locale.gettext('Electric Heat');
    case PoolsideDaemonHvacModes.ELECTRIC_COOL: return Locale.gettext('Electric Cool');
    case PoolsideDaemonHvacModes.GAS_HEAT: return Locale.gettext('Gas Heat');
    case PoolsideDaemonHvacModes.ELECTRIC_HEAT_AND_GAS_HEAT: return Locale.gettext('Gas & Electric heat');
    case PoolsideDaemonHvacModes.SOLAR: return Locale.gettext('Solar');
    case PoolsideDaemonHvacModes.COOL: return Locale.gettext('Cool');
    case PoolsideDaemonHvacModes.HEAT: return Locale.gettext('Heat');
    case PoolsideDaemonHvacModes.OFF: return Locale.gettext('Off');
    default: return value;
    }
}

/**
 * Specifies button order and default mode to set when needed.
 */
export const PoolsideDaemonHvacModesOrder = [
    PoolsideDaemonHvacModes.ELECTRIC_HEAT,
    PoolsideDaemonHvacModes.ELECTRIC_COOL,
    PoolsideDaemonHvacModes.GAS_HEAT,
    PoolsideDaemonHvacModes.ELECTRIC_HEAT_AND_GAS_HEAT,
    PoolsideDaemonHvacModes.SOLAR,
    PoolsideDaemonHvacModes.COOL,
    PoolsideDaemonHvacModes.HEAT,
];
